import { createColumnHelper } from '@tanstack/react-table'
import { StyleSheet, css } from 'aphrodite'
import type React from 'react'
import { useCallback, useEffect, useState } from 'react'
import GooglePlacesSelect from '../components/GooglePlacesSelect'
import Modal from '../components/Modal'
import Select from '../components/Select'
import SubmitButton from '../components/SubmitButton'
import Table from '../components/dashboard/Table'
import OnboardingInput from '../components/onboarding/OnboardingInput'
import { useUser } from '../contexts/UserContext'
import { type TruckResponse, truckService } from '../services/truckService'
import { userService } from '../services/userService'
import type { TruckCreate, User } from '../types/user'
import { TRUCK_TYPE_OPTIONS, getTruckTypeLabel } from '../types/workorder'

interface DriverTruckRow {
  id: string
  driverName: string
  driverEmail: string
  driverPhone: string
  stakeholderTypes: string[]
  companyName: string
  truckNumber: string
  truckType: string
  licenseNumber: string
  yardAddress: string
  hasMultipleTrucks: boolean
  isFirstTruckRow: boolean
  trucksCount: number
  driver: User
  truck: TruckResponse | null
  driverId: number
}

export const TrucksPage: React.FC = () => {
  const { user } = useUser()
  const [trucksData, setTrucksData] = useState<DriverTruckRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [companyName, setCompanyName] = useState<string>('')
  const [activeTab, setActiveTab] = useState<'company' | 'my'>('company')
  const [showAddTruckModal, setShowAddTruckModal] = useState(false)
  const [myTrucks, setMyTrucks] = useState<TruckResponse[]>([])
  const [myTrucksLoading, setMyTrucksLoading] = useState(false)
  const [showEditTruckModal, setShowEditTruckModal] = useState(false)
  const [editingTruck, setEditingTruck] = useState<{
    truck: TruckResponse
    driverId: number
  } | null>(null)
  const [isDeleting, setIsDeleting] = useState(false)
  const [companyDrivers, setCompanyDrivers] = useState<User[]>([])

  const isDriver = user?.user_type === 'DRIVER'
  const isManager =
    user?.user_type &&
    ['OWNER', 'PROJECT_MANAGER', 'ADMINISTRATOR'].includes(user.user_type)

  const fetchMyTrucks = useCallback(async () => {
    if (!user?.id) return

    try {
      setMyTrucksLoading(true)
      const trucks = await truckService.getUserVehicles(user.id)
      setMyTrucks(trucks)
    } catch (error) {
      console.error('Failed to fetch user trucks:', error)
    } finally {
      setMyTrucksLoading(false)
    }
  }, [user?.id])

  useEffect(() => {
    if (isDriver && activeTab === 'my') {
      fetchMyTrucks()
    }
  }, [isDriver, activeTab, fetchMyTrucks])

  const fetchDriversAndTrucks = useCallback(async () => {
    try {
      setLoading(true)
      setError(null)

      if (!user) {
        setError('User not authenticated')
        return
      }

      // Get all team members (users from the same company) and filter for drivers
      const teamMembers = await userService.getTeamMembers()

      const drivers = teamMembers.filter(
        (member) => member.user_type === 'DRIVER',
      )

      // Store drivers for the dropdown in AddTruckModal
      setCompanyDrivers(drivers)

      // Get company name for display
      try {
        const companyData = await userService.getUserCompanyPublic(user.id)
        if (companyData?.name) {
          setCompanyName(companyData.name)
        }
      } catch (err) {
        console.log('Could not fetch company name:', err)
      }

      // Get trucks for each driver and create table rows
      const tableRows: DriverTruckRow[] = []

      for (const driver of drivers) {
        try {
          const trucks = await truckService.getUserVehicles(driver.id)

          if (trucks.length === 0) {
            // Driver with no trucks
            tableRows.push({
              id: `driver-${driver.id}-no-trucks`,
              driverName: `${driver.first_name} ${driver.last_name}`,
              driverEmail: driver.email,
              driverPhone: driver.phone_number || 'N/A',
              stakeholderTypes: driver.stakeholder_types || [],
              companyName: driver.company?.name || '',
              truckNumber: 'No trucks assigned',
              truckType: '-',
              licenseNumber: '-',
              yardAddress: '-',
              hasMultipleTrucks: false,
              isFirstTruckRow: true,
              trucksCount: 0,
              driver,
              truck: null,
              driverId: driver.id,
            })
          } else {
            // Driver with trucks
            trucks.forEach((truck, index) => {
              tableRows.push({
                id: `driver-${driver.id}-truck-${truck.id}`,
                driverName: `${driver.first_name} ${driver.last_name}`,
                driverEmail: driver.email,
                driverPhone: driver.phone_number || 'N/A',
                stakeholderTypes: driver.stakeholder_types || [],
                companyName: driver.company?.name || '',
                truckNumber: truck.truck_number,
                truckType: truck.truck_type,
                licenseNumber: truck.license_number,
                yardAddress: truck.yard_address,
                hasMultipleTrucks: trucks.length > 1,
                isFirstTruckRow: index === 0,
                trucksCount: trucks.length,
                driver,
                truck,
                driverId: driver.id,
              })
            })
          }
        } catch (err) {
          console.error(`Failed to fetch trucks for driver ${driver.id}:`, err)
          // Still add the driver even if truck fetch fails
          tableRows.push({
            id: `driver-${driver.id}-error`,
            driverName: `${driver.first_name} ${driver.last_name}`,
            driverEmail: driver.email,
            driverPhone: driver.phone_number || 'N/A',
            stakeholderTypes: driver.stakeholder_types || [],
            companyName: driver.company?.name || '',
            truckNumber: 'Error loading trucks',
            truckType: '-',
            licenseNumber: '-',
            yardAddress: '-',
            hasMultipleTrucks: false,
            isFirstTruckRow: true,
            trucksCount: 0,
            driver,
            truck: null,
            driverId: driver.id,
          })
        }
      }

      setTrucksData(tableRows)
    } catch (err) {
      console.error('Failed to fetch drivers and trucks:', err)
      setError('Failed to load trucks data')
    } finally {
      setLoading(false)
    }
  }, [user])

  useEffect(() => {
    fetchDriversAndTrucks()
  }, [fetchDriversAndTrucks])

  const handleEditTruck = (truck: TruckResponse, driverId: number) => {
    setEditingTruck({ truck, driverId })
    setShowEditTruckModal(true)
  }

  const handleDeleteTruck = async (truck: TruckResponse, driverId: number) => {
    if (
      !window.confirm(
        `Are you sure you want to delete truck "${truck.truck_number}"?`,
      )
    ) {
      return
    }

    try {
      setIsDeleting(true)
      await truckService.deleteTruck(driverId, truck.id)
      // Refresh the data
      if (activeTab === 'my') {
        fetchMyTrucks()
      } else {
        fetchDriversAndTrucks()
      }
    } catch (err) {
      console.error('Failed to delete truck:', err)
      alert('Failed to delete truck. Please try again.')
    } finally {
      setIsDeleting(false)
    }
  }

  const canManageTruck = (row: DriverTruckRow): boolean => {
    if (!user) return false
    // Drivers can only manage their own trucks
    if (isDriver) {
      return row.driverId === user.id
    }
    // Managers can manage any truck in their company
    return isManager || false
  }

  const columnHelper = createColumnHelper<DriverTruckRow>()

  const columns = [
    columnHelper.accessor('driverName', {
      header: 'Driver',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('driverEmail', {
      header: 'Email',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('driverPhone', {
      header: 'Phone',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('stakeholderTypes', {
      header: 'Stakeholder Type',
      cell: (info) => {
        const row = info.row.original
        const types = info.getValue()
        const companyName = row.companyName
        const typeDisplay = types.length > 0 ? types.join(', ') : '-'
        if (companyName) {
          return `${typeDisplay} (${companyName})`
        }
        return typeDisplay
      },
    }),
    columnHelper.accessor('truckNumber', {
      header: 'Truck Number',
      cell: (info) => {
        const value = info.getValue()
        const row = info.row.original
        return (
          <span className={css(row.truck === null && styles.noTrucksText)}>
            {value}
          </span>
        )
      },
    }),
    columnHelper.accessor('truckType', {
      header: 'Truck Type',
      cell: (info) => {
        const value = info.getValue()
        if (value === '-') return value
        // Try to get the label, fallback to the raw value
        return (
          getTruckTypeLabel(value as Parameters<typeof getTruckTypeLabel>[0]) ||
          value
        )
      },
    }),
    columnHelper.accessor('licenseNumber', {
      header: 'License Number',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor('yardAddress', {
      header: 'Yard Address',
      cell: (info) => info.getValue(),
    }),
    columnHelper.display({
      id: 'actions',
      header: 'Actions',
      cell: (info) => {
        const row = info.row.original
        const truck = row.truck

        // No actions for rows without trucks
        if (!truck) {
          return null
        }

        // Check if user can manage this truck
        if (!canManageTruck(row)) {
          return null
        }

        return (
          <div className={css(styles.actionsCell)}>
            <button
              className={css(styles.actionButton, styles.editButton)}
              onClick={() => truck && handleEditTruck(truck, row.driverId)}
              title="Edit truck"
              disabled={isDeleting}
            >
              <i className="fa-solid fa-pencil" />
            </button>
            <button
              className={css(styles.actionButton, styles.deleteButton)}
              onClick={() => truck && handleDeleteTruck(truck, row.driverId)}
              title="Delete truck"
              disabled={isDeleting}
            >
              <i className="fa-solid fa-trash" />
            </button>
          </div>
        )
      },
    }),
  ]

  // AddTruckModal component
  const AddTruckModal: React.FC<{
    onClose: () => void
    onSuccess: () => void
    drivers: User[]
    showDriverSelect: boolean
  }> = ({ onClose, onSuccess, drivers, showDriverSelect }) => {
    const [formData, setFormData] = useState<TruckCreate>({
      truck_number: '',
      truck_type: '',
      license_number: '',
      yard_address: '',
    })
    const [selectedDriverId, setSelectedDriverId] = useState<number | null>(
      // If not showing driver select, default to current user
      showDriverSelect ? null : user?.id || null,
    )
    const [yardAddressValue, setYardAddressValue] = useState<{
      label: string
      value: string
    } | null>(null)
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [modalError, setModalError] = useState<string | null>(null)

    // Build driver options for the select
    const driverOptions = drivers.map((driver) => ({
      value: String(driver.id),
      label: `${driver.first_name} ${driver.last_name}`,
    }))

    const handleInputChange =
      (field: keyof TruckCreate) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }))
      }

    const handleSubmit = async () => {
      if (!selectedDriverId) {
        setModalError('Please select a driver')
        return
      }

      try {
        setIsSubmitting(true)
        setModalError(null)
        await truckService.addTruck(selectedDriverId, formData)
        onSuccess()
        onClose()
      } catch (err) {
        setModalError(
          err instanceof Error ? err.message : 'Failed to add truck',
        )
      } finally {
        setIsSubmitting(false)
      }
    }

    return (
      <Modal onClose={onClose}>
        <div className={css(styles.modalContent)}>
          <h2 className={css(styles.modalTitle)}>New Truck</h2>
          <div className={css(styles.modalForm)}>
            {showDriverSelect && (
              <div className={css(styles.formGroup)}>
                <label className={css(styles.formLabel)}>
                  Assign to Driver
                </label>
                <Select
                  placeholder="Select a driver"
                  options={driverOptions}
                  value={
                    selectedDriverId
                      ? driverOptions.find(
                          (opt) => opt.value === String(selectedDriverId),
                        )
                      : undefined
                  }
                  onChange={(option) => {
                    setSelectedDriverId(option ? Number(option.value) : null)
                  }}
                />
              </div>
            )}
            <OnboardingInput
              label="Truck Number"
              value={formData.truck_number}
              onChange={handleInputChange('truck_number')}
              placeholder="Enter truck number"
            />
            <div className={css(styles.formGroup)}>
              <label className={css(styles.formLabel)}>Truck Type</label>
              <Select
                placeholder="Select truck type"
                options={TRUCK_TYPE_OPTIONS}
                value={
                  formData.truck_type
                    ? TRUCK_TYPE_OPTIONS.find(
                        (opt) => opt.value === formData.truck_type,
                      )
                    : undefined
                }
                onChange={(option) => {
                  setFormData((prev) => ({
                    ...prev,
                    truck_type: option?.value || '',
                  }))
                }}
              />
            </div>
            <OnboardingInput
              label="License Number"
              value={formData.license_number}
              onChange={handleInputChange('license_number')}
              placeholder="Enter license number"
            />
            <div className={css(styles.formGroup)}>
              <label className={css(styles.formLabel)}>Yard Address</label>
              <GooglePlacesSelect
                placeholder="Enter yard address"
                value={yardAddressValue}
                onChange={(option) => {
                  setYardAddressValue(option)
                  setFormData((prev) => ({
                    ...prev,
                    yard_address: option?.value || '',
                  }))
                }}
              />
            </div>
          </div>
          {modalError && (
            <div className={css(styles.modalError)}>{modalError}</div>
          )}
          <div className={css(styles.modalActions)}>
            <button className={css(styles.cancelButton)} onClick={onClose}>
              Cancel
            </button>
            <SubmitButton
              onClick={handleSubmit}
              canSubmit={
                !isSubmitting && (showDriverSelect ? !!selectedDriverId : true)
              }
              text={isSubmitting ? 'Adding...' : 'Add Truck'}
            />
          </div>
        </div>
      </Modal>
    )
  }

  // EditTruckModal component
  const EditTruckModal: React.FC<{
    onClose: () => void
    onSuccess: () => void
    truck: TruckResponse
    driverId: number
  }> = ({ onClose, onSuccess, truck, driverId }) => {
    const [formData, setFormData] = useState<TruckCreate>({
      truck_number: truck.truck_number,
      truck_type: truck.truck_type,
      license_number: truck.license_number,
      yard_address: truck.yard_address,
    })
    const [yardAddressValue, setYardAddressValue] = useState<{
      label: string
      value: string
    } | null>(
      truck.yard_address
        ? { label: truck.yard_address, value: truck.yard_address }
        : null,
    )
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [modalError, setModalError] = useState<string | null>(null)

    const handleInputChange =
      (field: keyof TruckCreate) =>
      (e: React.ChangeEvent<HTMLInputElement>) => {
        setFormData((prev) => ({ ...prev, [field]: e.target.value }))
      }

    const handleSubmit = async () => {
      try {
        setIsSubmitting(true)
        setModalError(null)
        await truckService.updateTruck(driverId, truck.id, formData)
        onSuccess()
        onClose()
      } catch (err) {
        setModalError(
          err instanceof Error ? err.message : 'Failed to update truck',
        )
      } finally {
        setIsSubmitting(false)
      }
    }

    return (
      <Modal onClose={onClose}>
        <div className={css(styles.modalContent)}>
          <h2 className={css(styles.modalTitle)}>Edit Truck</h2>
          <div className={css(styles.modalForm)}>
            <OnboardingInput
              label="Truck Number"
              value={formData.truck_number}
              onChange={handleInputChange('truck_number')}
              placeholder="Enter truck number"
            />
            <div className={css(styles.formGroup)}>
              <label className={css(styles.formLabel)}>Truck Type</label>
              <Select
                placeholder="Select truck type"
                options={TRUCK_TYPE_OPTIONS}
                value={
                  formData.truck_type
                    ? TRUCK_TYPE_OPTIONS.find(
                        (opt) => opt.value === formData.truck_type,
                      )
                    : undefined
                }
                onChange={(option) => {
                  setFormData((prev) => ({
                    ...prev,
                    truck_type: option?.value || '',
                  }))
                }}
              />
            </div>
            <OnboardingInput
              label="License Number"
              value={formData.license_number}
              onChange={handleInputChange('license_number')}
              placeholder="Enter license number"
            />
            <div className={css(styles.formGroup)}>
              <label className={css(styles.formLabel)}>Yard Address</label>
              <GooglePlacesSelect
                placeholder="Enter yard address"
                value={yardAddressValue}
                onChange={(option) => {
                  setYardAddressValue(option)
                  setFormData((prev) => ({
                    ...prev,
                    yard_address: option?.value || '',
                  }))
                }}
              />
            </div>
          </div>
          {modalError && (
            <div className={css(styles.modalError)}>{modalError}</div>
          )}
          <div className={css(styles.modalActions)}>
            <button className={css(styles.cancelButton)} onClick={onClose}>
              Cancel
            </button>
            <SubmitButton
              onClick={handleSubmit}
              canSubmit={!isSubmitting}
              text={isSubmitting ? 'Saving...' : 'Save Changes'}
            />
          </div>
        </div>
      </Modal>
    )
  }

  if (loading) {
    return (
      <div className={css(styles.trucksDashboard)}>
        <div className={css(styles.mainContent)}>
          <div className={css(styles.loadingContainer)}>
            <i className="fa-duotone fa-solid fa-spinner-third fa-spin fa-3x" />
            <p className={css(styles.loadingText)}>Loading trucks...</p>
          </div>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={css(styles.trucksDashboard)}>
        <div className={css(styles.mainContent)}>
          <div className={css(styles.errorContainer)}>
            <i className="fa-solid fa-exclamation-triangle fa-3x" />
            <p className={css(styles.errorText)}>{error}</p>
          </div>
        </div>
      </div>
    )
  }

  return (
    <div className={css(styles.trucksDashboard)}>
      <div className={css(styles.mainContent)}>
        <div className={css(styles.header)}>
          <div className={css(styles.titleSection)}>
            <h2 className={css(styles.title)}>
              {isDriver && activeTab === 'my'
                ? 'My Trucks'
                : 'Drivers & Trucks'}
            </h2>
            {isDriver ? (
              <div className={css(styles.filterButtons)}>
                <button
                  className={css(
                    styles.filterButton,
                    activeTab === 'company' && styles.activeFilter,
                  )}
                  onClick={() => setActiveTab('company')}
                >
                  Drivers & Trucks
                </button>
                <button
                  className={css(
                    styles.filterButton,
                    activeTab === 'my' && styles.activeFilter,
                  )}
                  onClick={() => setActiveTab('my')}
                >
                  My Trucks
                </button>
              </div>
            ) : (
              <p className={css(styles.subtitle)}>
                All trucks assigned to drivers in{' '}
                {companyName || 'your company'}
              </p>
            )}
          </div>
          {/* Show Add Truck button for drivers in "My Trucks" tab or for managers in company view */}
          {((isDriver && activeTab === 'my') ||
            (isManager && activeTab === 'company')) && (
            <div className={css(styles.buttonContainer)}>
              <button
                className={css(styles.addButton)}
                onClick={() => setShowAddTruckModal(true)}
              >
                <i className="fa-solid fa-plus" /> New Truck
              </button>
            </div>
          )}
        </div>
        <div className={css(styles.tableContainer)}>
          <div className={css(styles.tableWrapper)}>
            {activeTab === 'my' && isDriver ? (
              myTrucksLoading ? (
                <div className={css(styles.loadingContainer)}>
                  <i className="fa-duotone fa-solid fa-spinner-third fa-spin fa-3x" />
                  <p className={css(styles.loadingText)}>
                    Loading your trucks...
                  </p>
                </div>
              ) : myTrucks.length === 0 ? (
                <div className={css(styles.emptyState)}>
                  <i className="fa-solid fa-truck fa-3x" />
                  <h3>No trucks found</h3>
                  <p>
                    You haven't added any trucks yet. Click "Add Truck" to get
                    started.
                  </p>
                </div>
              ) : (
                <Table
                  data={myTrucks.map((truck) => ({
                    id: `my-truck-${truck.id}`,
                    driverName: `${user?.first_name} ${user?.last_name}`,
                    driverEmail: user?.email || '',
                    driverPhone: user?.phone_number || 'N/A',
                    stakeholderTypes: user?.stakeholder_types || [],
                    companyName: user?.company?.name || '',
                    truckNumber: truck.truck_number,
                    truckType: truck.truck_type,
                    licenseNumber: truck.license_number,
                    yardAddress: truck.yard_address,
                    hasMultipleTrucks: false,
                    isFirstTruckRow: true,
                    trucksCount: 1,
                    driver: user as User,
                    truck,
                    driverId: user?.id || 0,
                  }))}
                  columns={columns}
                />
              )
            ) : trucksData.length === 0 ? (
              <div className={css(styles.emptyState)}>
                <i className="fa-solid fa-truck fa-3x" />
                <h3>No drivers found</h3>
                <p>No drivers found in your company</p>
              </div>
            ) : (
              <Table data={trucksData} columns={columns} />
            )}
          </div>
        </div>
      </div>
      {showAddTruckModal && (
        <AddTruckModal
          onClose={() => setShowAddTruckModal(false)}
          onSuccess={() => {
            // Refresh the appropriate data based on context
            if (isManager) {
              fetchDriversAndTrucks()
            } else {
              fetchMyTrucks()
            }
            setShowAddTruckModal(false)
          }}
          drivers={companyDrivers}
          showDriverSelect={isManager || false}
        />
      )}
      {showEditTruckModal && editingTruck && (
        <EditTruckModal
          onClose={() => {
            setShowEditTruckModal(false)
            setEditingTruck(null)
          }}
          onSuccess={() => {
            if (activeTab === 'my') {
              fetchMyTrucks()
            } else {
              fetchDriversAndTrucks()
            }
            setShowEditTruckModal(false)
            setEditingTruck(null)
          }}
          truck={editingTruck.truck}
          driverId={editingTruck.driverId}
        />
      )}
    </div>
  )
}

const styles = StyleSheet.create({
  trucksDashboard: {
    display: 'flex',
    padding: '16px',
    backgroundColor: '#F9FAFB',
    minHeight: '100vh',
    width: 'calc(100vw - 280px)',
    boxSizing: 'border-box',
    overflowX: 'hidden',
    '@media (max-width: 1024px)': {
      padding: '12px',
      width: '100vw',
    },
  },
  mainContent: {
    flex: 1,
    width: '100%',
    boxSizing: 'border-box',
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: '20px',
    padding: '20px',
    '@media (max-width: 1024px)': {
      flexDirection: 'column',
      alignItems: 'stretch',
      gap: '12px',
      padding: '12px',
    },
  },
  titleSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  title: {
    margin: 0,
    fontSize: '24px',
    fontWeight: 600,
    color: '#1F2937',
  },
  subtitle: {
    margin: 0,
    fontSize: '14px',
    color: '#6B7280',
  },
  tableContainer: {
    borderRadius: '8px',
    padding: '16px',
    width: '100%',
    boxSizing: 'border-box',
    backgroundColor: '#F9FAFB',
    overflowX: 'hidden',
  },
  tableWrapper: {
    width: '100%',
    overflowX: 'auto',
    backgroundColor: '#F9FAFB',
    display: 'block',
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#6B7280',
  },
  loadingText: {
    marginTop: '16px',
    fontSize: '16px',
  },
  errorContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    height: '50vh',
    color: '#EF4444',
  },
  errorText: {
    marginTop: '16px',
    fontSize: '16px',
  },
  emptyState: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    padding: '48px',
    color: '#9CA3AF',
    textAlign: 'center',
  },
  noTrucksText: {
    color: '#9CA3AF',
    fontStyle: 'italic',
  },
  modalContent: {
    padding: '32px',
    backgroundColor: 'white',
    borderRadius: '12px',
    maxWidth: '500px',
    width: '90%',
    margin: '0 auto',
    position: 'relative',
    maxHeight: '90vh',
    overflow: 'auto',
  },
  modalTitle: {
    fontSize: '24px',
    fontWeight: 600,
    color: '#1F2937',
    marginBottom: '32px',
    textAlign: 'center',
  },
  modalForm: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
    marginBottom: '32px',
  },
  formGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  },
  formLabel: {
    fontSize: '14px',
    fontWeight: 500,
    color: '#374151',
  },
  modalError: {
    color: '#DC2626',
    fontSize: '14px',
    marginBottom: '20px',
    textAlign: 'center',
    padding: '12px',
    backgroundColor: '#FEF2F2',
    borderRadius: '6px',
    border: '1px solid #FECACA',
  },
  modalActions: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
    paddingTop: '8px',
  },
  cancelButton: {
    padding: '8px 16px',
    backgroundColor: '#E5E7EB',
    color: '#374151',
    border: 'none',
    borderRadius: '6px',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#D1D5DB',
    },
  },
  filterButtons: {
    display: 'flex',
    gap: '8px',
    marginTop: '10px',
  },
  filterButton: {
    backgroundColor: '#E5E7EB',
    color: '#4B5563',
    padding: '6px 12px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    ':hover': {
      backgroundColor: '#D1D5DB',
    },
  },
  activeFilter: {
    backgroundColor: '#1C64F2',
    color: 'white',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  buttonContainer: {
    display: 'flex',
    gap: '8px',
  },
  addButton: {
    backgroundColor: '#1C64F2',
    color: 'white',
    padding: '8px 16px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '14px',
    fontWeight: 500,
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    ':hover': {
      backgroundColor: '#1955CC',
    },
  },
  actionsCell: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
  },
  actionButton: {
    padding: '6px 10px',
    borderRadius: '4px',
    border: 'none',
    cursor: 'pointer',
    fontSize: '12px',
    transition: 'background-color 0.2s',
    ':disabled': {
      opacity: 0.5,
      cursor: 'not-allowed',
    },
  },
  editButton: {
    backgroundColor: '#E5E7EB',
    color: '#374151',
    ':hover': {
      backgroundColor: '#D1D5DB',
    },
  },
  deleteButton: {
    backgroundColor: '#FEE2E2',
    color: '#DC2626',
    ':hover': {
      backgroundColor: '#FECACA',
    },
  },
})
